<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form @submit.prevent="saveCenter">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('Create center') }}</h1>
                    </div>
                </div>
            </div>
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6 shadow sm:rounded-lg">
                    <loading :status="loading"/>
                    <div class="bg-white md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Center details') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('This information will be displayed publicly') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="name">{{ $t('Name') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="name"
                                            v-model="center.name"
                                            :placeholder="$t('Name')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Emails') }}</label>
                                    <input-email
                                        id="emails"
                                        ref="emails"
                                        :emailList="center.emails"
                                    ></input-email>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="phone">{{ $t('Phone') }}</label>
                                    <input-phone
                                        id="phones"
                                        ref="phones"
                                        :phoneList="center.phones"
                                    ></input-phone>
                                </div>
                            </div>
                        </div>
                        <div class="md:col-span-3">
                            <div class="py-3">
                                <div class="border-t border-gray-200"></div>
                            </div>
                        </div>
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Center settings') }}</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">
                                {{ $t('Center settings') }}.
                            </p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="id_ext">{{ $t('External ID') }}</label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input
                                            id="id_ext"
                                            v-model="center.id_ext"
                                            :placeholder="$t('External ID')"
                                            class="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            type="text"
                                        >
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="lang_id">{{ $t('Languages') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <select
                                            id="lang_id"
                                            v-model="center.lang_id"
                                            class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                                            required
                                        >
                                            <option :value="null" disabled>{{ $t('Select an option') }}</option>
                                            <option v-for="language in languages" :value="language.id">{{ language.name }}</option>
                                        </select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="org">{{ $t('Organization') }}<strong class="text-red-600"> *</strong></label>
                                    <div class="mt-1 relative rounded-md shadow-sm">
                                        <input-select
                                            id="org"
                                            v-model.lazy="center.org_id"
                                            :options="orgs"
                                            option-label="name"
                                            required
                                            searchable
                                        >
                                            <template v-slot:selectedOption="props">
                                                <template v-if="props && !props.anySelected">
                                                    <span class="block truncate">{{ $t('Select an option') }}</span>
                                                </template>
                                                <template v-else>
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <span class="block truncate">{{ props.option.name }}</span>
                                                    <div v-if="props.option.phones.length > 0" class="hidden xl:flex xl:items-center xl:gap-2 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-3 w-3 text-gray-500" icon="font-awesome.phone-solid"></svg-vue>
                                                        <span v-for="(phone, index) in props.option.phones">{{ phone.country_code == '34' ? phone.number : "+" + phone.country_code + phone.number }}</span>
                                                    </div>
                                                </template>
                                            </template>
                                            <template v-slot:selectOption="props">
                                                <div class="flex items-center space-x-3">
                                                    <div class="flex-shrink-0 inline-block">
                                                        <img :src="props.option.avatar" alt="Icon" class="w-5 h-auto rounded-full object-scale-down">
                                                    </div>
                                                    <div class="font-normal block truncate">
                                                        {{ props.option.name }}
                                                    </div>
                                                    <div v-if="props.option.phones.length > 0" class="hidden xl:flex xl:items-center xl:gap-2 truncate font-normal text-gray-500">
                                                        <svg-vue class="h-3 w-3 text-gray-500" icon="font-awesome.phone-solid"></svg-vue>
                                                        <span v-for="(phone, index) in props.option.phones">{{ phone.country_code == '34' ? phone.number : "+" + phone.country_code + phone.number }}</span>
                                                    </div>
                                                </div>
                                            </template>
                                        </input-select>
                                    </div>
                                </div>
                                <div class="col-span-3">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="status">{{ $t('Status') }}</label>
                                    <input-switch
                                        id="status"
                                        v-model="center.status"
                                        :disabled-label="$t('The center is disabled')"
                                        :enabled-label="$t('The center is activated')"
                                    ></input-switch>
                                    <div class="mt-2 relative text-xs text-gray-500">
                                        {{ $t('When the center is deactivated, the users in this center will not be able to create tickets') }}.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-gray-100 text-right px-4 py-3 sm:px-6">
                        <span class="inline-flex">
                            <router-link
                                class="btn btn-secondary shadow-sm rounded-md mr-4"
                                to="/dashboard/admin/centers"
                            >
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                class="btn btn-green shadow-sm rounded-md"
                                type="submit"
                            >
                                {{ $t('Create center') }}
                            </button>
                        </span>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "new-center",
    metaInfo() {
        return {
            title: this.$i18n.t('Create center')
        }
    },
    data() {
        return {
            languages: [],
            orgs: [],
            center: {
                id: null,
                id_ext: null,
                name: null,
                avatar: null,
                //avatar_preview: null,
                emails: [],
                phones: [],
                user_ids: null,
                lang_id: null,
                org_id: null,
                status: true,
            }
        }
    },
    computed: {
        loading() {
            return this.$store.state.loading.states.main;
        },
    },
    beforeMount() {
        this.$store.commit('resetLoading');
    },
    mounted() {
        this.getLanguages();
        this.getOrgs();
    },
    methods: {
        async saveCenter() {
            const self = this;
            self.$store.commit('loadSections');
            self.center.phones = [...await self.$refs.phones.savePhones()];
            self.center.emails = [...await self.$refs.emails.saveEmails()];
            axios.post('api/dashboard/admin/centers', self.center).then(function (response) {
                self.$store.commit('unloadSections');
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                self.$router.push('/dashboard/admin/centers/' + response.data.center.id + '/edit');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getLanguages() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/languages').then(function (response) {
                self.languages = response.data;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        },
        getOrgs() {
            const self = this;
            self.$store.commit('loadSections');
            axios.get('api/dashboard/admin/orgs/list', {
                params: {
                    include: ["server", "phones"]
                }
            }).then(function (response) {
                self.orgs = response.data.orgs;
                self.$store.commit('unloadSections');
            }).catch(function () {
                self.$store.commit('unloadSections');
            });
        }
    }
}
</script>
